import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import {
  CUPCAKE_FORM_PRESET,
  isCupcakeProductPreset,
} from './utils/orderFormLinks';
import StepOne from './steps/StepOneCustomerInfo';
import StepThree from './steps/StepThreeCakeDetails';
import StepFive from './steps/StepFiveReview';
import '../../index.css';
import StepFour from './steps/StepFourDecorations'; // new line
import axios from 'axios';
import { getServerUrl } from '../../utils/serverUrl';


const MultiStepCakeOrderForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOrderingEnabled, setIsOrderingEnabled] = useState(true);
  const [summerTimingEnabled, setSummerTimingEnabled] = useState(false);

  const SERVER_URL = getServerUrl();

  const getStepFromURL = useCallback(() => {
    const params = new URLSearchParams(location.search);
    return parseInt(params.get('step'), 10) || 1;
  }, [location.search]);

  const buildOrderSearch = useCallback(
    (stepNum) => {
      const params = new URLSearchParams(location.search);
      params.set('step', String(stepNum));
      return params.toString();
    },
    [location.search]
  );

  const navigateToStep = useCallback(
    (stepNum) => {
      navigate({ pathname: '/order', search: buildOrderSearch(stepNum) });
    },
    [navigate, buildOrderSearch]
  );

  const [step, setStep] = useState(getStepFromURL());

  const createInitialFormData = useCallback(() => {
    const params = new URLSearchParams(location.search);
    const base = {
      firstName: '',
      lastName: '',
      customerEmail: '',
      customerPhone: '',
      eventDate: '',
      eventTime: '',
      cakeShape: '',
      cakeSize: '',
      baseCakePrice: '',
      servings: '',
      cakeFlavor: '',
      fillingFlavor: '',
      frosting: '',
      decorationStyle: '',
      cakeTopper: '',
      topperType: '',
      cupcakeDozens: 0,
      cupcakeUnitConfigs: [],
      cupcakeConfigMode: 'uniform',
      cupcakeFlavorSelections: [],
      icingSelections: [],
      sugarSprinkleColors: [],
      cupcakeIcingTintEnabled: false,
      cupcakeIcingTintColor: '',
      deliveryCost: '',
      decorations_price: '',
      price: '',
    };
    if (isCupcakeProductPreset(params)) {
      return { ...base, ...CUPCAKE_FORM_PRESET };
    }
    return base;
  }, [location.search]);

  const [formData, setFormData] = useState(() => createInitialFormData());

  const nextStep = () => {
    const newStep = Math.min(step + 1, 4);
    navigateToStep(newStep);
  };

  const prevStep = () => {
    const newStep = Math.max(step - 1, 1);
    navigateToStep(newStep);
  };


  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (isCupcakeProductPreset(params)) {
      setFormData((prev) =>
        prev.cakeShape === 'Cupcakes'
          ? prev
          : { ...prev, ...CUPCAKE_FORM_PRESET }
      );
    }
  }, [location.search]);

  useEffect(() => {
    setStep(getStepFromURL());
  }, [getStepFromURL]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  useEffect(() => {
    axios
      .get(`${SERVER_URL}/api/settings/order-status`)
      .then((res) => {
        setIsOrderingEnabled(res.data.isOrderingEnabled);
        setSummerTimingEnabled(Boolean(res.data.summerTimingEnabled));
      })
      .catch((err) => {
        console.error('⚠️ Failed to fetch ordering status:', err.message);
        setIsOrderingEnabled(true);
      });
  }, [SERVER_URL]);



  const steps = {
    1: (
      <StepOne
        formData={formData}
        setFormData={setFormData}
        nextStep={nextStep}
        summerTimingEnabled={summerTimingEnabled}
      />
    ),
    2: <StepThree formData={formData} setFormData={setFormData} nextStep={nextStep} prevStep={prevStep} />,
    3: <StepFour formData={formData} setFormData={setFormData} nextStep={nextStep} prevStep={prevStep} />,
    4: <StepFive formData={formData} prevStep={prevStep} />,
  };

  // if (checkingStatus) {
  //   return <p className="text-center mt-10 text-gray-500">Checking order status...</p>;
  // }
  if (!isOrderingEnabled) {
    return (
      <div className="min-h-dvh bg-candy-background px-4 py-10 sm:py-12">
        <div className="w-full max-w-3xl mx-auto bg-white border border-candy-border rounded-xl shadow-md p-6 sm:p-8 text-center">
          <h2 className="text-xl sm:text-2xl font-semibold text-candy-primary font-hobo mb-4">
            🚫 Orders Temporarily Closed
          </h2>
          <p className="text-base sm:text-lg text-candy-text/80 leading-relaxed">
            We're currently <span className="font-semibold underline">not accepting</span> online orders.
            <br />
            📞 Please contact <strong>Candy Haven & Cakes</strong> directly to place your order.
          </p>
        </div>
      </div>
    );
  }



  return (
    <div className="min-h-dvh bg-candy-background px-3 sm:px-4 py-6 sm:py-12">
      <div className="w-full max-w-4xl mx-auto bg-white border border-candy-border rounded-2xl shadow-xl p-4 sm:p-6 md:p-8 min-w-0 overflow-x-hidden">
        <h1 className="text-center text-2xl sm:text-3xl md:text-4xl font-bold text-candy-text font-hobo mb-6 sm:mb-8">
          Step {step} of 4
        </h1>
        {steps[step]}
      </div>
    </div>
  );
};

export default MultiStepCakeOrderForm;
